import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Accordion } from '@/design-system/atoms/Accordion';
import { Button } from '@/design-system/atoms/Button';
import { RadioGroup } from '@/design-system/atoms/Forms/RadioGroup';
import { Radio } from '@/design-system/atoms/Forms/RadioGroup/Radio/Radio';
import { LazyIconX } from '@/design-system/atoms/Icons/IconX/Lazy';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useLocationFilterContext } from '../../../contexts/LocationFilterContext';
import { useLocationUIContext } from '../../../contexts/LocationUIContext';
import { useLocationViewState } from '../../../hooks/useLocationViewState';
import styles from '../LocationFilter.module.scss';

const LocationFilterMobile = ({
    accordionItems,
    sortValue,
    sortCriteria,
    handleSort,
    distance,
    setDistance,
    disabledDistanceOptions,
    handleSingleFilter,
    handleResetAll,
}) => {
    const { openPanel, setOpenPanel } = useLocationFilterContext();

    const viewState = useLocationViewState();

    const history = useHistory();
    const searchParams = useSearchParams();
    const initialSearchParamsRef = React.useRef();
    React.useEffect(() => {
        if (openPanel === false) {
            return;
        }

        initialSearchParamsRef.current = searchParams.toString();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPanel]);

    const { resetAllLabel, resetAllAriaLabel, seeResultsLabel, seeResultsAriaLabel } =
        useLocationUIContext();

    const filterFooterRef = useRef();
    const [footerPadding, setFooterPadding] = useState(0);

    useEffect(() => {
        const footerHeight = filterFooterRef.current?.getBoundingClientRect().height;
        setFooterPadding(footerHeight);
    }, []);

    return (
        <form
            className={classNames(
                styles[`location-filter`],
                openPanel && styles[`location-filter--open`],
            )}
            {...(!openPanel ? { inert: '' } : {})}
        >
            <fieldset
                className={classNames(
                    styles[`location-filter__fieldset`],
                    styles[`location-filter__wrapper`],
                )}
            >
                <span className={styles[`location-filter__intro`]}>
                    <legend className={classNames(styles[`location-filter__headline`], 'type-h4')}>
                        Filter & Sort
                    </legend>
                    <Button
                        ariaLabel="Close Filter & Sort"
                        buttonStyle={Button.STYLE.TERTIARY}
                        iconComponent={LazyIconX}
                        className={styles[`location-filter__close`]}
                        type="button"
                        onClick={() => {
                            const path = window.location.pathname;
                            const newUrl = `${path}?${initialSearchParamsRef.current}`;
                            history.push(newUrl);

                            setOpenPanel(false);
                        }}
                    />
                </span>

                {viewState === 'result-list' && (
                    <fieldset className={styles[`location-filter__fieldset`]}>
                        <legend
                            className={classNames(
                                styles[`location-filter__group-legend`],
                                'type-e1',
                            )}
                        >
                            Sort By:
                        </legend>
                        <RadioGroup
                            className={styles[`location-filter__radio-group`]}
                            value={sortValue}
                            onChange={handleSort}
                        >
                            {sortCriteria.map(([name]) => {
                                if (name === 'Distance' && disabledDistanceOptions) {
                                    return null;
                                }
                                return <Radio key={name} value={name} label={name} />;
                            })}
                        </RadioGroup>
                    </fieldset>
                )}

                <fieldset className={styles[`location-filter__fieldset`]}>
                    <legend
                        className={classNames(styles[`location-filter__group-legend`], 'type-e1')}
                    >
                        Filter By:
                    </legend>
                    {!disabledDistanceOptions && (
                        <RadioGroup
                            label="Distance"
                            labelClass={styles[`location-filter__radio-group-label`]}
                            value={distance}
                            onChange={(value) => {
                                setDistance(value);
                                handleSingleFilter(
                                    { distance: value },
                                    {
                                        componentTitle: 'Distance',
                                        actionLabel: value ? `Within ${value} miles` : 'Any',
                                    },
                                );
                            }}
                        >
                            <Radio value="" label="Any" />
                            <Radio value="2" label="Within 2 miles" />
                            <Radio value="10" label="Within 10 miles" />
                            <Radio value="25" label="Within 25 miles" />
                            <Radio value="50" label="Within 50 miles" />
                        </RadioGroup>
                    )}
                </fieldset>
                <Accordion
                    className={styles[`location-filter__accordion`]}
                    accordionItems={accordionItems}
                    style={{ paddingBottom: `${footerPadding}px` }}
                    noScrollLogic
                />
            </fieldset>
            <div className={styles[`location-filter__footer`]} ref={filterFooterRef}>
                <Button
                    buttonStyle={Button.STYLE.SECONDARY}
                    className={styles[`location-filter__reset`]}
                    type="button"
                    label={resetAllLabel}
                    ariaLabel={resetAllAriaLabel}
                    onClick={() => {
                        setOpenPanel(false);
                        handleResetAll();
                    }}
                    analytics={{
                        componentName: 'Filter Sort Panel',
                        componentTitle: 'filter_panel',
                        interactionType: 'filter_reset',
                        allFilters: searchParams,
                    }}
                />
                <Button
                    buttonStyle={Button.STYLE.PRIMARY}
                    className={styles[`location-filter__apply`]}
                    type="button"
                    label={seeResultsLabel}
                    ariaLabel={seeResultsAriaLabel}
                    onClick={() => {
                        setOpenPanel(false);
                    }}
                />
            </div>
        </form>
    );
};

LocationFilterMobile.propTypes = {};

export default LocationFilterMobile;
